import type React from 'react';
import { Hammer, Palette, HeartPulse, Compass, TrendingUp, BookOpen, Shield } from 'lucide-react';

/**
 * The 7 archetype Occupational **Paths** (OCCUPATIONAL_DOMAIN_SPEC §3).
 *
 * A Path is a professional "class" that organizes Roles. The 7 Paths ARE the
 * domain's AREAS — the rail + radar (`useOccupationalAreaNodes`), each its own
 * page holding the roles on it. (Before OC0 the code conflated Paths with the old
 * value-sector parameters: `CareerRole.primary_career_path` was typed as
 * `OccupationalParameterName`. OC0 separated them; the paths-as-areas restructure
 * then retired the interim 7 lens areas entirely.)
 *
 * ⚠️ These names are pinned by three DB CHECK constraints (migration 0155:
 * `career_roles`, `work_deliverables`, `project_occupational_settings`) — renaming
 * one needs a migration. Display-only names are free: use `professionalName`.
 *
 * Each Path carries a crest (icon + color), a professional name, a curated
 * catalog of example roles the user can pick from (+ a custom-role escape hatch,
 * wired in OC2), and the path-name stored in the DB (`career_roles.primary_career_path`).
 */
export type OccupationalPathName =
  | 'Builder'
  | 'Creator'
  | 'Healer'
  | 'Guide'
  | 'Trader'
  | 'Scholar'
  | 'Guardian';

export interface OccupationalPath {
  /** Stored verbatim in the DB (`career_roles.primary_career_path`). */
  name: OccupationalPathName;
  /** The human-facing professional framing. */
  professionalName: string;
  description: string;
  coreCapability: string;
  /** Crest icon (Lucide component). */
  icon: React.ElementType;
  /** Crest color (also the path accent). */
  color: string;
  /** Curated role catalog — the picker options for OC2 (+ custom). */
  exampleRoles: string[];
}

export const OCCUPATIONAL_PATHS: Record<OccupationalPathName, OccupationalPath> = {
  Builder: {
    name: 'Builder',
    professionalName: 'Creation & Operations',
    description:
      'Bring systems, products, and structures into being. Builders turn ideas into working things — they engineer, construct, ship, and operate.',
    coreCapability: 'Engineering, construction, systems, delivery',
    icon: Hammer,
    color: '#3B82F6',
    exampleRoles: [
      'Software Engineer',
      'Architect',
      'Developer',
      'Operations Manager',
      'Product Manager',
      'Systems Designer',
      'Industrial Designer',
      'Civil Engineer',
    ],
  },
  Creator: {
    name: 'Creator',
    professionalName: 'Arts, Design & Media',
    description:
      'Give form, meaning, and experience. Creators express ideas through aesthetics and story — they design, write, compose, and craft.',
    coreCapability: 'Aesthetic intelligence, storytelling, form-giving',
    icon: Palette,
    color: '#A855F7',
    exampleRoles: [
      'Graphic Designer',
      'Interior Designer',
      'UX Designer',
      'Artist',
      'Writer',
      'Filmmaker',
      'Photographer',
      'Content Creator',
    ],
  },
  Healer: {
    name: 'Healer',
    professionalName: 'Health, Care & Wellbeing',
    description:
      'Restore and sustain human wellbeing. Healers care, treat, and support — they tend to bodies, minds, and lives.',
    coreCapability: 'Care, healing, human support systems',
    icon: HeartPulse,
    color: '#EF4444',
    exampleRoles: [
      'Doctor',
      'Nurse',
      'Therapist',
      'Psychologist',
      'Fitness Coach',
      'Wellness Coach',
      'Nutritionist',
      'Physiotherapist',
    ],
  },
  Guide: {
    name: 'Guide',
    professionalName: 'Education, Leadership & Advisory',
    description:
      'Develop people and direct effort. Guides teach, advise, and lead — they grow capability in others and steer groups toward goals.',
    coreCapability: 'Teaching, advising, leadership, facilitation',
    icon: Compass,
    color: '#F97316',
    exampleRoles: [
      'Teacher',
      'Trainer',
      'Consultant',
      'Coach',
      'Mentor',
      'Manager',
      'Facilitator',
      'Community Leader',
    ],
  },
  Trader: {
    name: 'Trader',
    professionalName: 'Business, Sales & Markets',
    description:
      'Create and exchange value through markets. Traders sell, build businesses, and move capital — they connect supply, demand, and growth.',
    coreCapability: 'Value exchange, growth, markets, deal-making',
    icon: TrendingUp,
    color: '#EAB308',
    exampleRoles: [
      'Salesperson',
      'Marketer',
      'Business Developer',
      'Entrepreneur',
      'Investor',
      'Broker',
      'Account Manager',
      'Growth Lead',
    ],
  },
  Scholar: {
    name: 'Scholar',
    professionalName: 'Research, Strategy & Knowledge',
    description:
      'Generate and apply knowledge. Scholars research, analyze, and strategize — they make sense of complexity and chart the path forward.',
    coreCapability: 'Research, analysis, strategy, systems thinking',
    icon: BookOpen,
    color: '#14B8A6',
    exampleRoles: [
      'Researcher',
      'Analyst',
      'Scientist',
      'Strategist',
      'Academic',
      'Data Analyst',
      'Economist',
      'Policy Analyst',
    ],
  },
  Guardian: {
    name: 'Guardian',
    professionalName: 'Governance, Protection & Law',
    description:
      'Protect order, rights, and integrity. Guardians govern, defend, and uphold — they maintain fairness, safety, and accountability.',
    coreCapability: 'Judgment, protection, governance, compliance',
    icon: Shield,
    color: '#EC4899',
    exampleRoles: [
      'Lawyer',
      'Judge',
      'Police Officer',
      'Security Specialist',
      'Compliance Officer',
      'Risk Manager',
      'Auditor',
      'Government Officer',
    ],
  },
};

export const OCCUPATIONAL_PATH_NAMES: OccupationalPathName[] = [
  'Builder',
  'Creator',
  'Healer',
  'Guide',
  'Trader',
  'Scholar',
  'Guardian',
];

/**
 * The 1:1 remap from the old value-sectors (the pre-OC0
 * `OccupationalParameterName`) to the new archetype Paths. Kept here so the UI
 * and the OC0 migration share one source of truth; safe to delete once no
 * legacy value-sector data remains.
 */
export const LEGACY_SECTOR_TO_PATH: Record<string, OccupationalPathName> = {
  Wellbeing: 'Healer',
  Society: 'Guide',
  Commerce: 'Trader',
  Ecology: 'Scholar',
  Innovation: 'Builder',
  Creativity: 'Creator',
  Justice: 'Guardian',
};

/**
 * Normalize a stored path value to an archetype Path. Maps legacy value-sectors
 * (rows not yet touched by the OC0 migration) to their new archetype and
 * defaults anything unknown to `Builder`, so the UI never reads an undefined
 * crest config whether or not the migration has run yet.
 */
export function resolvePathName(value: string | null | undefined): OccupationalPathName {
  if (value && value in OCCUPATIONAL_PATHS) return value as OccupationalPathName;
  if (value && LEGACY_SECTOR_TO_PATH[value]) return LEGACY_SECTOR_TO_PATH[value];
  return 'Builder';
}
